import { createElement } from 'react'
import type { Banner as StoryblokBanner } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { RichTextRenderer } from '@percent/cause-dashboard/common/components/storyblok/richTextRenderer/RichTextRenderer'
import type { BannerCta, BannerProps } from '@percent/cause-dashboard/app/productOffers/bannersCarousel/banner/Banner'
import {
  MarketplaceProgrammeVisitedSource,
  OriginCategory
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import { resolveNativeApplyPartnerKey } from '@percent/cause-dashboard/constants/nativeApply/resolveNativeApplyPartner'
import { getResolvedNativeApplyContext } from '@percent/cause-dashboard/constants/nativeApply/getResolvedNativeApplyContext'

export type MarketplaceBannerCarouselItem = BannerProps & {
  id: string
  controls: 'light' | 'dark'
}

/** Maps Storyblok `cta_label_color` option to theme tokens for inline CTA label styles. */
const CTA_LABEL_TEXT_COLOR_WHITE = 'var(--colors-white)'
const CTA_LABEL_TEXT_COLOR_BLACK = 'var(--colors-black)'

/** Storyblok native color picker field shape (types stay `unknown` in generated `storyblok-components.d.ts`). */
type StoryblokNativeColorPicker = {
  _uid?: string
  color?: string
  plugin?: string
}

function isStoryblokNativeColorPicker(value: unknown): value is StoryblokNativeColorPicker {
  if (value === null || typeof value !== 'object') {
    return false
  }
  return typeof (value as StoryblokNativeColorPicker).color === 'string'
}

/** Resolves `background_color` / `cta_background_color` from plain string or native-color-picker plugin payload. */
function storyblokColorFieldToCss(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim()
  }
  if (isStoryblokNativeColorPicker(value)) {
    const hex = value.color?.trim()
    if (hex) {
      return hex
    }
  }
  return null
}

function labelColorToCss(color: StoryblokBanner['cta_label_color']): string {
  return color === 'white' ? CTA_LABEL_TEXT_COLOR_WHITE : CTA_LABEL_TEXT_COLOR_BLACK
}

/**
 * Builds the discriminated `BannerCta`. Returns `null` when the underlying CMS state would render
 * a broken CTA — a `link` slide with no URL, or a `native_apply` slide without a resolvable partner
 * or resolvable offer relation. Any `null` here drops the entire slide upstream.
 */
function buildBannerCta(slide: StoryblokBanner): (BannerCta & { textColor: string; backgroundColor: string }) | null {
  const ctaText = slide.cta_label?.trim() ?? ''
  if (!ctaText) {
    return null
  }

  const ctaBackgroundColor = storyblokColorFieldToCss(slide.cta_background_color)
  if (!ctaBackgroundColor) {
    return null
  }

  const textColor = labelColorToCss(slide.cta_label_color)

  if (slide.cta_type === 'register_interest') {
    return {
      kind: 'register_interest',
      text: ctaText,
      textColor,
      backgroundColor: ctaBackgroundColor
    }
  }

  if (slide.cta_type === 'native_apply') {
    const partnerKey = resolveNativeApplyPartnerKey(slide.native_apply_partner)
    const modalContext = getResolvedNativeApplyContext(slide.offer)
    if (!partnerKey || !modalContext) {
      return null
    }
    return {
      kind: 'native_apply',
      text: ctaText,
      textColor,
      backgroundColor: ctaBackgroundColor,
      partnerKey,
      modalContext
    }
  }

  const ctaUrl = slide.cta_url?.trim() ?? ''
  if (!ctaUrl) {
    return null
  }
  return {
    kind: 'link',
    text: ctaText,
    textColor,
    backgroundColor: ctaBackgroundColor,
    url: ctaUrl
  }
}

/**
 * Maps one Storyblok `banner` blok to carousel item props, or `null` when asset URLs, color fields
 * or CTA payload cannot be resolved.
 */
export function mapStoryblokBannerSlideToCarouselItem(slide: StoryblokBanner): MarketplaceBannerCarouselItem | null {
  const logoUrl = slide.logo?.filename?.trim() ?? ''
  const imageUrl = slide.image?.filename?.trim() ?? ''

  if (!logoUrl || !imageUrl) {
    return null
  }

  const backgroundColor = storyblokColorFieldToCss(slide.background_color)
  if (!backgroundColor) {
    return null
  }

  const cta = buildBannerCta(slide)
  if (!cta) {
    return null
  }

  const description = createElement(RichTextRenderer, {
    document: slide.description,
    variant: 'nestedInCard'
  })

  return {
    id: slide._uid,
    controls: slide.arrows_theme,
    logo: logoUrl,
    image: imageUrl,
    backgroundColor,
    description,
    descriptionColor: slide.description_color,
    cta,
    experimentVariant: slide.experiment_variant || undefined,
    tracking: {
      programmeName: slide.analytics_tracking_name?.trim() ?? '',
      source: MarketplaceProgrammeVisitedSource.Banner,
      originCategory: OriginCategory.AppStoreCarousel
    }
  }
}
